import { DASHBOARD_CONTEXT } from '@trustero/trustero-api-web/lib/dashboard/dashboard_pb'
import { FRAMEWORK_MODEL_IDS } from '../../../../Utils/globalEnums'
import { FilterBarConfig } from './FilterBar.constants'

export type SearchBarOptions = {
  withContentSearch?: boolean
}

export type FilterBarProps = {
  config: FilterBarConfig
  resizeNodeId?: string
  searchBarOptions?: SearchBarOptions
  /** Renders inside the filter bar BEFORE the filters. Use for page-level controls
   *  that aren't filters themselves (e.g. an OE dashboard "Group by" dropdown). */
  leadingElement?: JSX.Element
  /** Renders AFTER the filter row, outside the wrapper. Use for trailing controls
   *  like Print/PDF buttons. */
  customElement?: JSX.Element
  dashboardContext?: DASHBOARD_CONTEXT
  dashboardName?: string
  /**
   * When true, expanded search + overflow shows a “See filters” control that collapses search.
   * When false/omitted, overflow uses the default “More filters” dropdown (unchanged).
   */
  useSeeFiltersWhenOverflow?: boolean
}

export enum FilterParam {
  DEPARTMENT = 'department_ids',
  POLICY = 'policy_ids',
  STATUS = 'status',
  OWNER = 'owner',
  SERVICE = 'related_service_ids',
  SOC2 = 'soc2_criteria',
  SOC1 = 'soc1_criteria',
  ISO = 'iso_27001_objectives',
  PCISAQD = 'pci_saq_d_objectives',
  PCISAQA = 'pci_saq_a_objectives',
  HITRUST = 'hitrust_objectives',
  HIPAA = 'hipaa_sections',
  NISTCSF = 'nist_csf_requirements',
  NYDFS = 'nydfs',
  ISO27701_DC = 'iso27701_dc',
  ISO27701_DP = 'iso27701_dp',
  GDPR_DC = 'gdpr_dc',
  GDPR_DP = 'gdpr_dp',
  PIPEDA_DC = 'pipeda_dc',
  PIPEDA_DP = 'pipeda_dp',
  TEST = 'test_framework_ids',
  HAS_DOCUMENTATION = 'has_documentation',
  IS_CUSTOMIZED = 'is_customized',
  AUDIT = 'audit',
  AUDIT_FRAMEWORKS = 'audit_frameworks',
  AUDIT_STATUS = 'audit_status',
  COMPLIANCE_FRAMEWORK = 'compliance_framework',
  INHERENT_RISK = 'inherent_risk',
  RESIDUAL_RISK = 'residual_risk',
  RECEPTOR = 'receptor_ids',
  SORT_ORDER = 'sort_order',
  SORT_COLUMN = 'sort_column',
  SERVICE_ROLE = 'service_roles',
  RISK_TIER = 'risk_tier',
  ATTESTATION = 'attestation_status',
  AI_CONTROL_CHECKS = 'ai_control_checks',
  CONTROL = 'control_ids',
  REQUEST = 'request_ids',
  SOURCE = 'sources',
  CREATED_SOURCE = 'created_source',
  FILE_TYPE = 'file_types',
  DATE_RANGE = 'date_range',
  PAGE = 'page',
  DOCUMENT_MANAGEMENT_SYSTEM = 'document_management_system',
  GRC_PLATFORM = 'grc_platform',
  EVIDENCE_TYPE = 'evidence_type',
  FOLDER_SCAN = 'folder_scan_ids',
  VISIBILITIES = 'visibilities',
  TAGS = 'tags',
  AUTOMATED = 'automated',
  SWIFT_CSCF_A1 = 'swift_cscf_a1',
  SWIFT_CSCF_A2 = 'swift_cscf_a2',
  SWIFT_CSCF_A3 = 'swift_cscf_a3',
  SWIFT_CSCF_A4 = 'swift_cscf_a4',
  SWIFT_CSCF_B = 'swift_cscf_b',
  SWIFT_PSCF = 'swift_pscf',
  TISAX_VDA_ISA = 'tisax_vda_isa',
  TRUST_PORTAL_REQUEST_ACCESS = 'status',
  TRUST_PORTAL_PERMISSION = 'status',
  TRUST_PORTAL_FEATURES = 'features',
  LADMF = 'ladmf',
  ORGANIZATION = 'organization',
  ASSIGNEE = 'assignee',
  QUESTIONNAIRE_STATUS = 'questionnaire_status',
  AUDIT_RUN_OWNER = 'audit_run_owner',
  AUDIT_RUN_CONTROL = 'audit_run_control',
  AUDIT_RUN_STATUS = 'audit_run_status',
  AUDIT_RUN_RESULT = 'audit_run_result',
  COMPANY = 'company',
  REPORT_TYPE = 'report_type',
  TRUST_SERVICE_CRITERIA = 'trust_service_criteria',
  AUDITOR = 'auditor',
  EXPIRATION_DATE = 'expiration_date',
  USER_ROLE = 'user_role',
  ACCESS_ROLE = 'access_role',
  EMAIL_NOTIFICATION = 'email_notification',
  USER_DEPARTMENT = 'user_department',
  DASHBOARD_ASSIGNEE = 'dashboard_assignee',
  DASHBOARD_AUDIT = 'dashboard_audit',
  DASHBOARD_RISK = 'dashboard_risk',
  DASHBOARD_DEPARTMENT = 'dashboard_department',
  DASHBOARD_FRAMEWORK = 'dashboard_framework',
  DASHBOARD_POLICY = 'dashboard_policy',
  DASHBOARD_DATE_RANGE = 'dashboard_date_range',
  DASHBOARD_CONTROL = 'dashboard_control',
  DASHBOARD_RISK_CATEGORY = 'dashboard_risk_category',
  DASHBOARD_VENDOR = 'dashboard_vendor',
  DASHBOARD_LABEL = 'dashboard_label',
  WORKFLOW_MANAGEMENT_SYSTEM = 'workflow_management_system',
  REPORT_CATEGORIES = 'report_categories',
  NIST800171 = 'nist_800_171',
  CCPA = 'ccpa',
  PCIDSS = 'pcidss',
  ISO27701 = 'iso27701',
  DODCMMC = 'dodcmmc',
  GDPR = 'gdpr',
  CUSTOM_FRAMEWORK = 'custom_framework',
  FRAMEWORK_TYPE = 'framework_type',
  NAME = 'name',
  CMMC_LEVEL_1 = 'cmmc_level_1',
  CMMC_LEVEL_2 = 'cmmc_level_2',
  DASHBOARD_NAME_DIMENSION = 'dashboard_name_dimension',
  DASHBOARD_EMAIL_DIMENSION = 'dashboard_email_dimension',
  DASHBOARD_COMPANY_DIMENSION = 'dashboard_company_dimension',
  DASHBOARD_TP_FEATURE_DIMENSION = 'dashboard_tp_feature_dimension',
  DASHBOARD_DOC_NAME_DIMENSION = 'dashboard_doc_name_dimension',
  DASHBOARD_TP_ACCESS_LEVEL_DIMENSION = 'dashboard_tp_access_level_dimension',
  DASHBOARD_TP_UPDATE_CATEGORY_DIMENSION = 'dashboard_tp_update_category_dimension',
  ISO27018 = 'iso27018',
  EXECUTION_TYPE = 'execution_type',
  PLAYBOOK_TYPE = 'playbook_type',
  PLAYBOOK_CATEGORY = 'playbook_category',
  COV_SEC_530 = 'cov_sec_530',
  ARTIFACT_TYPE = 'artifact_types',
  REQUEST_USE_CASE_TYPE = 'use_case_type',
  LABEL = 'label_ids',
  SECTION = 'section_ids',
}

export enum FilterName {
  DEPARTMENT = 'Department',
  POLICY = 'Policy',
  STATUS = 'Status',
  RECEPTOR_STATUS = 'Receptor Status',
  OWNER = 'Owner',
  SERVICE = 'Service',
  SOC2 = 'SOC 2 Criteria',
  SOC1 = 'SOC 1 Criteria',
  ISO = 'ISO 27001 Controls',
  PCISAQD = 'PCI DSS SAQ D Requirements',
  PCISAQA = 'PCI DSS SAQ A Requirements',
  HITRUST = 'HITRUST e1 BUIDS',
  HIPAA = 'HIPAA sections',
  NISTCSF = 'NIST CSF Requirements',
  NYDFS = 'NYDFS Requirements',
  ISO27701_DC = 'ISO 27701 - Data Controller Controls',
  ISO27701_DP = 'ISO 27701 - Data Processor Controls',
  GDPR_DC = 'GDPR - Data Controller Objectives',
  GDPR_DP = 'GDPR - Data Processor Objectives',
  PIPEDA_DC = 'PIPEDA - Data Controller Objectives',
  PIPEDA_DP = 'PIPEDA - Data Processor Objectives',
  TEST = 'Test Compliance Framework Objectives',
  HAS_DOCUMENTATION = 'Documentation',
  IS_CUSTOMIZED = 'Customized',
  AUDIT = 'Audit',
  AUDIT_FRAMEWORKS = 'Framework',
  AUDIT_STATUS = 'Status',
  COMPLIANCE_FRAMEWORK = 'Framework',
  INHERENT_RISK = 'Inherent Risk',
  RESIDUAL_RISK = 'Residual Risk',
  RECEPTOR = 'Receptor',
  SERVICE_ROLE = 'Service Role',
  RISK_TIER = 'Risk Tier',
  ATTESTATION = 'Attestation',
  AI_CONTROL_CHECKS = 'Control Tests',
  SOURCE = 'Source',
  FILE_TYPE = 'File Type',
  CONTROL = 'Linked Control',
  EVIDENCE_TYPE = 'Type',
  FOLDER_SCAN = 'Folder Scan',
  VISIBILITIES = 'Visibilities',
  TAGS = 'Tags',
  FRAMEWORK_TYPE = 'Type',
  NAME = 'Name',
  SWIFT_CSCF_A1 = 'Swift CSCF A1 Controls',
  SWIFT_CSCF_A2 = 'Swift CSCF A2 Controls',
  SWIFT_CSCF_A3 = 'Swift CSCF A3 Controls',
  SWIFT_CSCF_A4 = 'Swift CSCF A4 Controls',
  SWIFT_CSCF_B = 'Swift CSCF B Controls',
  SWIFT_PSCF = 'Swift PSCF Controls',
  TISAX_VDA_ISA = 'TISAX VDA ISA Controls',
  TRUST_PORTAL_REQUEST_ACCESS = 'Request Status',
  TRUST_PORTAL_PERMISSION = 'Status',
  TRUST_PORTAL_FEATURES = 'Access Feature',
  ORGANIZATION = 'Organization',
  ASSIGNEE = 'Assignee',
  QUESTIONNAIRE_STATUS = 'Status',
  AUDIT_RUN_OWNER = 'Owner',
  AUDIT_RUN_CONTROL = 'Control',
  AUDIT_RUN_STATUS = 'Status',
  AUDIT_RUN_RESULT = 'Result',
  COMPANY = 'Company',
  REPORT_TYPE = 'Report Type',
  TRUST_SERVICE_CRITERIA = 'Trust Service Criteria',
  AUDITOR = 'Auditor',
  EXPIRATION_DATE = 'Expiration Date',
  USER_ROLE = 'User Role',
  EMAIL_NOTIFICATION = 'Email Notification',
  USER_DEPARTMENT = 'Department',
  DASHBOARD_ASSIGNEE = 'Assignee',
  DASHBOARD_AUDIT = 'Audit',
  DASHBOARD_RISK = 'Risk',
  DASHBOARD_DEPARTMENT = 'Department',
  DASHBOARD_FRAMEWORK = 'Framework',
  DASHBOARD_POLICY = 'Policy',
  DASHBOARD_DATE_RANGE = 'Date Range',
  DASHBOARD_CONTROL = 'Control',
  DASHBOARD_RISK_CATEGORY = 'Category',
  DASHBOARD_VENDOR = 'Vendor',
  DASHBOARD_LABEL = 'Label',
  REPORT_CATEGORIES = 'Category',
  CMMC_LEVEL_1 = 'CMMC Level 1',
  CMMC_LEVEL_2 = 'CMMC Level 2',
  DASHBOARD_NAME_DIMENSION = 'Name',
  DASHBOARD_EMAIL_DIMENSION = 'Email',
  DASHBOARD_COMPANY_DIMENSION = 'Company',
  DASHBOARD_TP_FEATURE_DIMENSION = 'Feature',
  DASHBOARD_DOC_NAME_DIMENSION = 'Document Name',
  DASHBOARD_TP_ACCESS_LEVEL_DIMENSION = 'Access Level',
  DASHBOARD_TP_UPDATE_CATEGORY_DIMENSION = 'Category',
  ISO27018 = 'ISO 27018',
  EXECUTION_TYPE = 'Job Execution Type',
  ACCESS_ROLE = 'Access Role',
  PLAYBOOK_TYPE = 'Playbooks',
  PLAYBOOK_CATEGORY = 'Category',
  REQUEST_USE_CASE_TYPE = 'Type',
  LABEL = 'Label',
  SECTION = 'Section',
}

interface FilterValues {
  filterName: string
  filterParam: string
}

export const getFilterValuesByFrameworkModelId = (
  modelId: string,
): FilterValues => {
  switch (modelId) {
    case FRAMEWORK_MODEL_IDS.SOC2:
      return {
        filterName: FilterName.SOC2,
        filterParam: FilterParam.SOC2,
      }
    case FRAMEWORK_MODEL_IDS.SOC1:
      return {
        filterName: FilterName.SOC1,
        filterParam: FilterParam.SOC1,
      }
    case FRAMEWORK_MODEL_IDS.ISO27001:
      return {
        filterName: FilterName.ISO,
        filterParam: FilterParam.ISO,
      }
    case FRAMEWORK_MODEL_IDS.TEST:
      return {
        filterName: FilterName.TEST,
        filterParam: FilterParam.TEST,
      }
    case FRAMEWORK_MODEL_IDS.PCISAQD:
      return {
        filterName: FilterName.PCISAQD,
        filterParam: FilterParam.PCISAQD,
      }
    case FRAMEWORK_MODEL_IDS.PCISAQA:
      return {
        filterName: FilterName.PCISAQA,
        filterParam: FilterParam.PCISAQA,
      }
    case FRAMEWORK_MODEL_IDS.HITRUST:
      return {
        filterName: FilterName.HITRUST,
        filterParam: FilterParam.HITRUST,
      }
    case FRAMEWORK_MODEL_IDS.HIPAA:
      return {
        filterName: FilterName.HIPAA,
        filterParam: FilterParam.HIPAA,
      }
    case FRAMEWORK_MODEL_IDS.NISTCSF:
      return {
        filterName: FilterName.NISTCSF,
        filterParam: FilterParam.NISTCSF,
      }
    case FRAMEWORK_MODEL_IDS.NYDFS:
      return {
        filterName: FilterName.NYDFS,
        filterParam: FilterParam.NYDFS,
      }
    case FRAMEWORK_MODEL_IDS.ISO27701_DC:
      return {
        filterName: FilterName.ISO27701_DC,
        filterParam: FilterParam.ISO27701_DC,
      }
    case FRAMEWORK_MODEL_IDS.ISO27701_DP:
      return {
        filterName: FilterName.ISO27701_DP,
        filterParam: FilterParam.ISO27701_DP,
      }
    case FRAMEWORK_MODEL_IDS.GDPR_DC:
      return {
        filterName: FilterName.GDPR_DC,
        filterParam: FilterParam.GDPR_DC,
      }
    case FRAMEWORK_MODEL_IDS.GDPR_DP:
      return {
        filterName: FilterName.GDPR_DP,
        filterParam: FilterParam.GDPR_DP,
      }
    case FRAMEWORK_MODEL_IDS.PIPEDA_DC:
      return {
        filterName: FilterName.PIPEDA_DC,
        filterParam: FilterParam.PIPEDA_DC,
      }
    case FRAMEWORK_MODEL_IDS.PIPEDA_DP:
      return {
        filterName: FilterName.PIPEDA_DP,
        filterParam: FilterParam.PIPEDA_DP,
      }
    case FRAMEWORK_MODEL_IDS.TISAX_VDA_ISA:
      return {
        filterName: FilterName.TISAX_VDA_ISA,
        filterParam: FilterParam.TISAX_VDA_ISA,
      }
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A1:
      return {
        filterName: FilterName.SWIFT_CSCF_A1,
        filterParam: FilterParam.SWIFT_CSCF_A1,
      }
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A2:
      return {
        filterName: FilterName.SWIFT_CSCF_A2,
        filterParam: FilterParam.SWIFT_CSCF_A2,
      }
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A3:
      return {
        filterName: FilterName.SWIFT_CSCF_A3,
        filterParam: FilterParam.SWIFT_CSCF_A3,
      }
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_A4:
      return {
        filterName: FilterName.SWIFT_CSCF_A4,
        filterParam: FilterParam.SWIFT_CSCF_A4,
      }
    case FRAMEWORK_MODEL_IDS.SWIFT_CSCF_B:
      return {
        filterName: FilterName.SWIFT_CSCF_B,
        filterParam: FilterParam.SWIFT_CSCF_B,
      }
    case FRAMEWORK_MODEL_IDS.SWIFT_PSCF:
      return {
        filterName: FilterName.SWIFT_PSCF,
        filterParam: FilterParam.SWIFT_PSCF,
      }
    default:
      return {
        filterName: FilterName.COMPLIANCE_FRAMEWORK,
        filterParam: FilterParam.COMPLIANCE_FRAMEWORK,
      }
  }
}
